/* StripeHealthCheckPanel — One-click health check for the entire payments
 * + shipping pipeline. Mounts in Admin → Services tab.
 *
 * Calls /api/admin/stripe/verify-all-divisions which:
 *   • Probes Stripe for managed products + prices via metadata
 *   • Resolves every catalog tier to a Stripe Price ID
 *   • Creates 1 ephemeral Stripe Checkout Session per division (no charge)
 *   • Verifies shipping zones + that the e-store accepts the country list
 *
 * Surfaces a green/yellow/red status per division, plus a checkout-readiness
 * grid and a shipping-zones table.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, RefreshCw, Truck, CreditCard, ExternalLink, ListChecks } from 'lucide-react';

const API = `${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;

function StatusPill({ ok, label }) {
  return ok ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 text-[10.5px] font-bold uppercase tracking-wider text-emerald-700">
      <CheckCircle2 className="w-3 h-3" /> {label || 'Ready'}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 border border-rose-200 px-2.5 py-0.5 text-[10.5px] font-bold uppercase tracking-wider text-rose-700">
      <XCircle className="w-3 h-3" /> {label || 'Issue'}
    </span>
  );
}

function DivisionRow({ k, name, summary, prodCount, priceCount }) {
  const synced = (summary?.products_missing || 0) === 0 && (summary?.prices_missing || 0) === 0 && (summary?.prices_drift || 0) === 0;
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-[#F2F2F4] last:border-0" data-testid={`health-division-${k}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-[13px] text-[#0e0e10]">{name}</span>
          {prodCount != null && <code className="text-[10.5px] text-[#a8871a] bg-[#fff8e7] px-1.5 py-0.5 rounded">{prodCount} products · {priceCount} prices</code>}
        </div>
        {summary && (
          <div className="text-[11px] text-[#6E6E73] mt-0.5">
            Products {summary.products_found}/{summary.products_expected} · Prices {summary.prices_found}/{summary.prices_expected}
            {summary.prices_drift > 0 && <span className="text-amber-700"> · {summary.prices_drift} drift</span>}
          </div>
        )}
      </div>
      <StatusPill ok={synced} label={synced ? 'Synced' : 'Issues'} />
    </div>
  );
}

export default function StripeHealthCheckPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ranOnce, setRanOnce] = useState(false);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API}/admin/stripe/verify-all-divisions`, { credentials: 'include' });
      if (!r.ok) {
        setError(`Health check failed: HTTP ${r.status}`);
      } else {
        setData(await r.json());
        setRanOnce(true);
      }
    } catch (e) {
      setError(`Health check error: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-run on first mount
  useEffect(() => { run(); }, [run]);

  const stripeOk = data?.stripe_active && !data?.stripe_skipped_reason;
  const checkout = data?.checkout_readiness || {};
  const shipping = data?.shipping || {};

  const allOk =
    stripeOk &&
    Object.values(checkout).every((c) => c?.ok) &&
    !!shipping.checkout_session_test?.ok;

  return (
    <section className="rounded-2xl border border-[#E5E5E7] bg-white" data-testid="stripe-health-check-panel">
      <header className="flex items-center justify-between gap-3 flex-wrap px-5 pt-5 pb-3 border-b border-[#F2F2F4]">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-[15px] font-extrabold text-[#0e0e10]">End-to-end Health Check</h3>
            {ranOnce && (allOk
              ? <StatusPill ok label="All systems ready" />
              : (data?.stripe_skipped_reason
                  ? <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 border border-zinc-200 px-2.5 py-0.5 text-[10.5px] font-bold uppercase tracking-wider text-zinc-700">Stripe inactive</span>
                  : <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2.5 py-0.5 text-[10.5px] font-bold uppercase tracking-wider text-amber-800"><AlertTriangle className="w-3 h-3" /> Issues found</span>))}
          </div>
          <p className="text-[12.5px] text-[#6E6E73] mt-0.5">
            Read-only verification across Stripe products + prices + checkout session creation + shipping zones.
            {data?.mode && <code className="ml-2 text-[11px] bg-zinc-100 px-1.5 py-0.5 rounded">{data.mode}</code>}
          </p>
        </div>
        <button
          type="button"
          onClick={run}
          disabled={loading}
          data-testid="stripe-health-rerun"
          className="inline-flex items-center gap-1.5 text-[11.5px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full text-[#0e0e10] disabled:opacity-50 transition-colors"
          style={{ background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 100%)' }}
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Running…' : 'Re-run'}
        </button>
      </header>

      {error && <div className="px-5 py-3 text-[12px] text-rose-700 bg-rose-50/40 border-b border-rose-100">{error}</div>}

      {data?.stripe_skipped_reason && (
        <div className="px-5 py-3 text-[12px] text-zinc-700 bg-zinc-50 border-b border-zinc-200">
          Stripe is inactive: <code className="bg-white px-1 rounded">{data.stripe_skipped_reason}</code>. Set a real <code className="bg-white px-1 rounded">STRIPE_API_KEY</code> in <code className="bg-white px-1 rounded">backend/.env</code> and redeploy.
        </div>
      )}

      {/* Division catalog state */}
      {data && stripeOk && (
        <div className="px-1 py-1">
          <DivisionRow k="systems" name="Systems (managed services)" summary={data.divisions.systems?.summary} prodCount={data.divisions.systems?.summary?.products_found} priceCount={data.divisions.systems?.summary?.prices_found} />
          <DivisionRow k="academy" name="Academy (programs & cohorts)" summary={data.divisions.academy?.summary} prodCount={data.divisions.academy?.summary?.products_found} priceCount={data.divisions.academy?.summary?.prices_found} />
          <DivisionRow k="services" name="Services (e-commerce store)" summary={data.divisions.services?.summary} prodCount={data.divisions.services?.summary?.products_found} priceCount={data.divisions.services?.summary?.prices_found} />
        </div>
      )}

      {/* Stripe Dashboard quick filter links — operator can jump straight to each division */}
      {data && stripeOk && (
        <div className="px-5 py-3 border-t border-[#F2F2F4] flex items-center gap-2 flex-wrap" data-testid="health-dashboard-shortcuts">
          <span className="text-[11px] uppercase tracking-wider font-bold text-[#6E6E73]">Open in Stripe Dashboard:</span>
          {['systems', 'academy', 'services'].map((d) => (
            <a key={d}
               href={`https://dashboard.stripe.com/products?search=metadata%3A%22zhelta_division%3D${d}%22`}
               target="_blank" rel="noopener noreferrer"
               data-testid={`stripe-dashboard-${d}`}
               className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border border-[#E5E5E7] bg-white hover:bg-[#fff8e7] hover:border-[#c9a227]/50 text-[#0e0e10] transition-colors">
              {d} <ExternalLink className="w-3 h-3" />
            </a>
          ))}
          <span className="ml-auto text-[11px] text-[#6E6E73]">
            <strong className="text-[#0e0e10]">{Object.values(data.divisions || {}).reduce((s, d) => s + (d?.summary?.payment_links_found || 0), 0)}</strong> Payment Links · <strong className="text-[#0e0e10]">{Object.values(data.divisions || {}).reduce((s, d) => s + (d?.summary?.manual_overrides || 0), 0)}</strong> manual overrides
          </span>
        </div>
      )}

      {/* Checkout readiness — 3 cards */}
      {data && stripeOk && (
        <div className="px-5 py-4 border-t border-[#F2F2F4]">
          <div className="flex items-center gap-2 mb-3">
            <CreditCard className="w-4 h-4 text-[#a8871a]" />
            <h4 className="text-[12.5px] font-extrabold uppercase tracking-wider text-[#0e0e10]">Checkout readiness</h4>
            <span className="text-[11px] text-[#6E6E73]">Live Stripe Checkout Sessions created (no charges)</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {['systems', 'academy', 'services'].map((k) => {
              const r = checkout[k];
              if (!r) return null;
              return (
                <div key={k} className={`rounded-lg border p-3 ${r.ok ? 'border-emerald-200 bg-emerald-50/50' : 'border-rose-200 bg-rose-50/40'}`} data-testid={`health-checkout-${k}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[12px] font-bold uppercase tracking-wider text-[#0e0e10]">{k}</span>
                    <StatusPill ok={r.ok} label={r.ok ? 'Session OK' : 'Failed'} />
                  </div>
                  {r.ok ? (
                    <div className="mt-2 text-[11px] text-[#6E6E73] space-y-0.5">
                      <div>Total: <strong className="text-[#0e0e10]">{r.amount_total_usd}</strong></div>
                      <div className="truncate" title={r.session_id}>Session: <code className="text-[10.5px]">{r.session_id?.slice(0, 26)}…</code></div>
                    </div>
                  ) : (
                    <div className="mt-2 text-[11px] text-rose-700">{r.error}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Shipping zones */}
      {data?.shipping && (
        <div className="px-5 py-4 border-t border-[#F2F2F4]" data-testid="health-shipping">
          <div className="flex items-center gap-2 mb-3">
            <Truck className="w-4 h-4 text-[#a8871a]" />
            <h4 className="text-[12.5px] font-extrabold uppercase tracking-wider text-[#0e0e10]">Shipping zones</h4>
            <span className="text-[11px] text-[#6E6E73]">{shipping.supported_countries?.length || 0} supported destinations · NO free-shipping threshold</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {(shipping.zones || []).map((z) => (
              <div key={z.key} className="rounded-lg border border-[#E5E5E7] bg-[#fafafa] p-3" data-testid={`health-zone-${z.key}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[12px] font-bold text-[#0e0e10]">{z.label}</span>
                  <code className="text-[11px] font-bold text-[#a8871a] bg-white px-2 py-0.5 rounded border border-[#fff0c1]">${z.rate_usd}</code>
                </div>
                <div className="mt-1.5 text-[10.5px] text-[#6E6E73] flex flex-wrap gap-1">
                  {z.countries.map((c) => <span key={c} className="bg-white border border-zinc-200 px-1 rounded">{c}</span>)}
                </div>
              </div>
            ))}
          </div>
          {shipping.checkout_session_test?.ok && (
            <div className="mt-3 text-[11px] text-emerald-700 inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" />
              E-store Checkout accepts the 15-country allowlist · session
              <code className="ml-1 text-[10.5px] bg-white px-1 rounded border border-emerald-200">{shipping.checkout_session_test.session_id?.slice(0, 22)}…</code>
            </div>
          )}
        </div>
      )}

      {/* Pricing-ladder validator — catches cohort >= standard or standard >= premium */}
      {data?.pricing_ladder && (
        <div className="px-5 py-4 border-t border-[#F2F2F4]" data-testid="health-pricing-ladder">
          <div className="flex items-center gap-2 mb-3">
            <ListChecks className="w-4 h-4 text-[#a8871a]" />
            <h4 className="text-[12.5px] font-extrabold uppercase tracking-wider text-[#0e0e10]">Pricing ladder</h4>
            <span className="text-[11px] text-[#6E6E73]">{data.pricing_ladder.checked_count} programs checked · canonical ladder: cohort &lt; standard &lt; premium</span>
          </div>
          {data.pricing_ladder.ok ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 text-[12px] text-emerald-800 inline-flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              Every program follows the canonical ladder. No inversions detected.
            </div>
          ) : (
            <div className="space-y-2">
              {data.pricing_ladder.warnings.map((w, i) => (
                <div key={i} className="rounded-lg border border-rose-200 bg-rose-50/50 p-3" data-testid={`ladder-warning-${i}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-rose-700" />
                      <span className="font-bold text-[12.5px] text-[#0e0e10]">{w.name}</span>
                      <code className="text-[10.5px] text-[#a8871a] bg-[#fff8e7] px-1.5 py-0.5 rounded">{w.division}</code>
                    </div>
                  </div>
                  <div className="mt-1.5 text-[11px] text-rose-800">{w.problem}</div>
                  <div className="mt-1 flex items-center gap-1.5 flex-wrap text-[10.5px]">
                    {(w.ladder || []).map((t, j) => (
                      <span key={j} className="bg-white border border-rose-200 px-1.5 py-0.5 rounded">
                        {t.tier} ${t.amount_usd}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Footer with checked_at */}
      {data?.checked_at && (
        <div className="px-5 py-2 text-[10.5px] text-[#6E6E73] border-t border-[#F2F2F4]" data-testid="health-footer">
          Last run: {new Date(data.checked_at).toLocaleString()}
          <a href="https://dashboard.stripe.com/products" target="_blank" rel="noopener noreferrer" className="ml-3 inline-flex items-center gap-1 text-[#a8871a] hover:underline">
            Open Stripe Dashboard <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      )}
    </section>
  );
}
